import { Capacitor } from '@capacitor/core';

// Plateforme réelle : vrai uniquement dans l'app Capacitor (iOS/Android).
const isNativePlatform = Capacitor.isNativePlatform();

/**
 * Mode aperçu, réservé au serveur de développement.
 *
 * Toute l'interface mobile est conditionnée à `isNative`, qui est faux dans un
 * navigateur : sans ce commutateur, la seule façon de voir l'app serait de la
 * compiler et de l'installer sur un téléphone. Ouvrir `?native=1` sur
 * `npm run serve` fait basculer l'affichage en mode application (barre
 * d'onglets, barre d'application, design system), ce qui permet de travailler
 * la mise en page dans le navigateur.
 *
 * Le drapeau est mémorisé pour la session de l'onglet, sinon il serait perdu
 * dès la première navigation du routeur.
 *
 * `process.env.NODE_ENV` est remplacé par une constante à la compilation :
 * dans le build de production, la condition est évaluée à false et tout ce
 * bloc est supprimé par le minifieur. Le site web ne peut donc pas l'activer.
 */
const isPreviewNative = (() => {
  if (process.env.NODE_ENV === 'production') return false;
  if (typeof window === 'undefined') return false;
  try {
    const asked = new URLSearchParams(window.location.search).get('native');
    if (asked === '1') window.sessionStorage.setItem('oc-preview-native', '1');
    if (asked === '0') window.sessionStorage.removeItem('oc-preview-native');
    return window.sessionStorage.getItem('oc-preview-native') === '1';
  } catch {
    return false;
  }
})();

// Utilisé partout pour n'afficher qu'en natif ce qui est propre à l'app, afin
// que le site web reste pixel-identique.
export const isNative = isNativePlatform || isPreviewNative;

// En natif, l'app est servie depuis https://localhost → les URLs relatives
// /api/* ne pointent pas vers le back, on force donc l'URL absolue.
// En web (dev et prod), on garde vide : proxy de dev (vue.config.js) en
// développement, même domaine en production.
// Volontairement basé sur la plateforme RÉELLE : en mode aperçu, on veut
// continuer à passer par le proxy de développement, sinon le navigateur
// bloquerait les appels pour cause de CORS.
export const API_BASE = isNativePlatform ? 'https://www.ouicsport.fr' : '';

/**
 * Renvoie l'URL de la photo dans sa définition d'origine.
 *
 * Les visuels d'offres sont servis sous la forme
 * `/static.ouicsport.fr/images/{largeur}x{hauteur}/{fichier}`. En retirant le
 * segment de taille, le serveur renvoie le fichier tel qu'il a été téléversé.
 *
 * Attention : la définition d'origine VARIE d'une offre à l'autre. Mesuré sur
 * le catalogue : les stages d'équitation font 1920x1080, tandis que les
 * sessions de canoë et de tennis ne font que 388x200 — plus petit, donc, que
 * la variante « 414x219 », que le serveur produisait en agrandissant. Il n'y a
 * pas de définition minimale garantie : à utiliser là où la photo doit être
 * grande, et conserver les variantes redimensionnées pour les vignettes, où
 * elles sont plus légères et suffisent.
 */
export const imageOriginale = (url) => {
  if (!url) return url;
  return String(url).replace(/\/\d+x\d+\//, '/');
};

// Helper pour URLs d'images : toujours utiliser ce wrapper dans les templates
// au lieu d'écrire /api/gallery_img/... en dur.
export const apiImg = (path) => {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path; // déjà absolue
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${clean}`;
};
